/**
 * Auth context. The bearer token lives in React state and module scope only — nothing is written
 * to localStorage/sessionStorage, so a page reload returns the user to the sign-in screen.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { auth as authApi } from './endpoints';
import { setAccessToken, setUnauthorizedHandler } from './api';
import { GatewayRole, GatewayRoleLabels } from './enums';
import type { CurrentUserDto } from './types';

// ---------------------------------------------------------------------------
// Permission model
// ---------------------------------------------------------------------------

export type Permission =
  | 'documents.upload'
  | 'documents.edit'
  | 'documents.post'
  | 'documents.refer'
  | 'documents.reject'
  | 'documents.retry'
  | 'documents.reconcile'
  | 'documents.resolveUnknown'
  | 'vendors.manage'
  | 'transactions.pull'
  | 'purchaseOrders.match'
  | 'approvals.decide'
  | 'rules.manage'
  | 'aiSettings.manage'
  | 'connection.manage'
  | 'users.manage'
  // Trading partners. Split the way the server splits them: drafting a disclosure and searching
  // the directory are operator work, but approving what is disclosed and changing who this
  // company exchanges documents with are administrator decisions.
  | 'directory.draft'
  | 'directory.publish'
  | 'partners.search'
  | 'partners.manage';

/**
 * Lowest role that holds each permission. The server is the real authority; this table only
 * decides what the UI offers, so a hidden button can never be the only thing standing between a
 * user and an action they should not perform.
 */
export const PermissionMinRole: Record<Permission, GatewayRole> = {
  'documents.upload': GatewayRole.Operator,
  'documents.edit': GatewayRole.Operator,
  'documents.post': GatewayRole.Operator,
  'documents.refer': GatewayRole.Operator,
  'documents.reject': GatewayRole.Operator,
  'documents.retry': GatewayRole.Operator,
  'documents.reconcile': GatewayRole.Operator,
  'documents.resolveUnknown': GatewayRole.Admin,
  'vendors.manage': GatewayRole.Operator,
  'transactions.pull': GatewayRole.Operator,
  'purchaseOrders.match': GatewayRole.Operator,
  'approvals.decide': GatewayRole.Approver,
  'rules.manage': GatewayRole.Admin,
  'aiSettings.manage': GatewayRole.Admin,
  'connection.manage': GatewayRole.Admin,
  'users.manage': GatewayRole.Admin,
  'directory.draft': GatewayRole.Operator,
  'directory.publish': GatewayRole.Admin,
  'partners.search': GatewayRole.Operator,
  'partners.manage': GatewayRole.Admin,
};

export interface AuthContextValue {
  user: CurrentUserDto | null;
  token: string | null;
  isAuthenticated: boolean;
  /** True while the initial /auth/me probe is in flight. */
  initialising: boolean;
  signInError: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  /**
   * Adopts a session the API issued without a password prompt — account activation and invitation
   * acceptance both hand back a token plus the user.
   */
  adoptSession: (accessToken: string, user: CurrentUserDto) => void;
  signOut: () => void;
  /** True when the signed-in user may perform the action. Read-only roles get false everywhere. */
  can: (permission: Permission) => boolean;
  /** True when the user's role is at least `role`. */
  hasRole: (role: GatewayRole) => boolean;
  /** Refresh the cached user (after a role change, for example). */
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<CurrentUserDto | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [signInError, setSignInError] = useState<string | null>(null);
  // Nothing is persisted, so there is no session to restore; the app starts ready.
  const [initialising] = useState(false);

  const signOut = useCallback(() => {
    setToken(null);
    setUser(null);
    setAccessToken(null);
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setToken(null);
      setUser(null);
      setAccessToken(null);
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    setSignInError(null);
    try {
      const response = await authApi.signIn({ email, password });
      setAccessToken(response.accessToken);
      setToken(response.accessToken);
      setUser(response.user);
    } catch (error) {
      setAccessToken(null);
      setToken(null);
      setUser(null);
      const message = error instanceof Error ? error.message : 'Sign-in failed.';
      setSignInError(message);
      throw error;
    }
  }, []);

  const adoptSession = useCallback((accessToken: string, nextUser: CurrentUserDto) => {
    setSignInError(null);
    setAccessToken(accessToken);
    setToken(accessToken);
    setUser(nextUser);
  }, []);

  const refreshUser = useCallback(async () => {
    const current = await authApi.me();
    setUser(current);
  }, []);

  const can = useCallback(
    (permission: Permission): boolean => {
      if (!user) return false;
      // An explicit server-issued permission always wins.
      if (user.permissions?.includes(permission)) return true;
      return user.role >= PermissionMinRole[permission];
    },
    [user],
  );

  const hasRole = useCallback(
    (role: GatewayRole): boolean => (user ? user.role >= role : false),
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      isAuthenticated: token !== null && user !== null,
      initialising,
      signInError,
      signIn,
      adoptSession,
      signOut,
      can,
      hasRole,
      refreshUser,
    }),
    [
      user,
      token,
      initialising,
      signInError,
      signIn,
      adoptSession,
      signOut,
      can,
      hasRole,
      refreshUser,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>.');
  return context;
}

// ---------------------------------------------------------------------------
// Route and section guards
// ---------------------------------------------------------------------------

/** Redirects anonymous users to the sign-in screen, remembering where they were headed. */
export function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to="/signin" replace state={{ from: location.pathname + location.search }} />;
  }
  return <>{children}</>;
}

export interface RequireRoleProps {
  minRole: GatewayRole;
  children: ReactNode;
  /** Rendered instead of the standard notice; pass `null` to render nothing at all. */
  fallback?: ReactNode;
}

/**
 * Gates a route or a section of a page on the user's role. Used both as a route element and
 * inline around mutating controls.
 */
export function RequireRole({ minRole, children, fallback }: RequireRoleProps): JSX.Element | null {
  const { user } = useAuth();
  const permitted = user !== null && user.role >= minRole;

  if (permitted) return <>{children}</>;
  if (fallback !== undefined) return <>{fallback}</>;

  return (
    <div className="panel restricted" role="note">
      <h2>Restricted</h2>
      <p>
        This area needs the <strong>{GatewayRoleLabels[minRole]}</strong> role or higher. You are
        signed in as <strong>{user ? GatewayRoleLabels[user.role] : 'a guest'}</strong>.
      </p>
    </div>
  );
}

/** Renders children only when the permission is held. Handy for hiding mutating buttons. */
export function Can({
  permission,
  children,
  fallback = null,
}: {
  permission: Permission;
  children: ReactNode;
  fallback?: ReactNode;
}): JSX.Element | null {
  const { can } = useAuth();
  return can(permission) ? <>{children}</> : <>{fallback}</>;
}
